// Shared publish flow — the single path that turns the creator's
// published drafts into a committed site. Called from onboarding (first
// publish) and from the composer (every publish after). Keeping it here
// means "publish" is defined once, not reimplemented per call site.

import { getSecret } from "./secrets";
import { getOnboarding } from "./storage/onboarding";
import { listPublished } from "./storage/drafts";
import { getProfile, profileWithDefaults } from "./profile";
import { defaultSiteUrl } from "./handle";
import { draftToPost, publishSite } from "./publish";
import type { CreatorProfile } from "./post";
import type { CommitResult } from "./github-commit";

/** Assemble the creator's feed/site identity from onboarding + the saved
 *  profile. Returns null if onboarding hasn't reached a handle yet. */
export async function buildProfile(sub: string): Promise<CreatorProfile | null> {
  const onb = await getOnboarding(sub);
  if (!onb?.handle) return null;
  const saved = profileWithDefaults(getProfile(sub));
  return {
    handle: onb.handle,
    displayName: saved.displayName.trim() || onb.handle,
    bio: saved.bio.trim() || "",
    // cf_pages_project holds the live site URL (set at first publish).
    siteUrl: onb.cf_pages_project || defaultSiteUrl(onb.handle),
    language: "en",
    discoverable: true,
    theme: saved.theme!,
    accent: saved.accent!,
    about: saved.about,
  };
}

export interface PublishOutcome {
  siteUrl: string;
  commit: CommitResult;
}

/** Render + commit the full public site from the creator's currently
 *  published drafts. Throws on missing auth / repo / profile. */
export async function publishAll(sub: string, message: string): Promise<PublishOutcome> {
  const token = getSecret("github_token", sub);
  const onb = await getOnboarding(sub);
  const profile = await buildProfile(sub);
  if (!token) throw new Error("No GitHub token — reconnect GitHub in Settings.");
  if (!onb?.public_repo) throw new Error("No public repo — finish onboarding first.");
  if (!profile) throw new Error("Profile not ready — finish onboarding first.");

  const published = await listPublished(sub);
  const posts = published.map(draftToPost);
  const commit = await publishSite(token, onb.public_repo, profile, posts, message);
  return { siteUrl: profile.siteUrl, commit };
}
